import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthProvider";
import { ApiError, isAbortError, userFacingError } from "../lib/api";
import {
  bulkCreateDistinct as apiBulkCreateDistinct,
  bulkDelete as apiBulkDelete,
  bulkReplace as apiBulkReplace,
  createWorkout,
  deleteSeries as apiDeleteSeries,
  deleteWorkout,
  listWorkouts,
  patchWorkout
} from "../lib/plannerApi";
import { removeTrainingPlan } from "../lib/trainingPlanApi";
import { notifyJourneyChanged } from "../lib/journeyApi";
import { invalidateFuelPlanCache } from "../lib/fuelPlanApi";
import type {
  BulkCreateItem,
  CalendarBounds,
  CreatePlannedWorkoutInput,
  PlannedWorkout,
  UpdatePlannedWorkoutInput
} from "../lib/plannerTypes";
import type { FuelPolicySnapshot } from "../lib/fuelPlanTypes";
import { classifyFuelTierFromPolicy } from "@shared/lib/fuelTiers";
import { addDaysYmd, compareYmd } from "../lib/dates";
import { PREFERENCES_SAVED_EVENT } from "../lib/preferencesSaved";
import { FUELING_PROFILE_CHANGED_EVENT } from "../lib/fuelingProfileApi";

/**
 * Server caps `from`–`to` spans at 400 days (see `src/modules/planner/routes.ts`
 * `MAX_LIST_WINDOW_DAYS`). When the planner has paged out to its maximum
 * adjacent-window span (~64 weeks = 448 days) we have to split the request
 * into chunks and merge. This stays internal to the hook — callers continue
 * to pass a single `{ from, to }` range.
 */
const MAX_FETCH_SPAN_DAYS = 380; // server cap is 400, leave headroom

function spanDays(range: { from: string; to: string }): number {
  // YMD-only math, locally — `addDaysYmd` and `compareYmd` both round-trip
  // correctly across DST boundaries.
  let count = 0;
  let cursor = range.from;
  while (compareYmd(cursor, range.to) <= 0) {
    cursor = addDaysYmd(cursor, 1);
    count += 1;
  }
  return count;
}

function chunkRange(range: { from: string; to: string }): Array<{ from: string; to: string }> {
  if (spanDays(range) <= MAX_FETCH_SPAN_DAYS) return [range];
  const chunks: Array<{ from: string; to: string }> = [];
  let chunkStart = range.from;
  while (compareYmd(chunkStart, range.to) <= 0) {
    const chunkEndCandidate = addDaysYmd(chunkStart, MAX_FETCH_SPAN_DAYS - 1);
    const chunkEnd =
      compareYmd(chunkEndCandidate, range.to) < 0 ? chunkEndCandidate : range.to;
    chunks.push({ from: chunkStart, to: chunkEnd });
    if (compareYmd(chunkEnd, range.to) >= 0) break;
    chunkStart = addDaysYmd(chunkEnd, 1);
  }
  return chunks;
}

/** Fetch a range, transparently splitting into ≤MAX_FETCH_SPAN_DAYS chunks
 * when the requested range exceeds the server cap. Merges workouts and
 * keeps the first chunk's policy (all chunks return the same policy snapshot). */
async function listWorkoutsChunked(
  range: { from: string; to: string },
  signal: AbortSignal
): Promise<{
  workouts: PlannedWorkout[];
  policy: FuelPolicySnapshot | null;
  calendarBounds: CalendarBounds | null;
}> {
  const chunks = chunkRange(range);
  if (chunks.length === 1) {
    const res = await listWorkouts(chunks[0], signal);
    return { ...res, calendarBounds: res.calendarBounds ?? null };
  }
  const results = await Promise.all(chunks.map((c) => listWorkouts(c, signal)));
  if (signal.aborted) {
    // Returning whatever we have wouldn't be wrong, but the caller already
    // bails on `signal.aborted`. Keep the shape sane regardless.
    return { workouts: [], policy: null, calendarBounds: null };
  }
  const seen = new Set<string>();
  const workouts: PlannedWorkout[] = [];
  for (const r of results) {
    for (const w of r.workouts) {
      if (seen.has(w.id)) continue;
      seen.add(w.id);
      workouts.push(w);
    }
  }
  workouts.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  // Bounds are whole-account (range-independent) — every chunk carries the
  // same value, so the first is as good as any.
  return {
    workouts,
    policy: results[0]?.policy ?? null,
    calendarBounds: results[0]?.calendarBounds ?? null
  };
}

/**
 * The data hook the PlannerScreen sits on. Owns the fetch lifecycle, the
 * optimistic-mutations layer (insert/update/delete the local cache first, roll
 * back on API failure), and a narrow `lastKnown` localStorage cache so the grid
 * paints instantly on refresh.
 *
 * Single source of truth for the planner's working set. The screen passes the
 * returned helpers to chips / add-modal / drag-n-drop without ever poking the
 * api module directly.
 */

// Base prefix for the localStorage paint-cache. The actual key is scoped by
// range (see `storageKey`) so two tabs/windows viewing different date ranges
// keep separate paint-caches and don't clobber each other on refresh.
const STORAGE_PREFIX = "fg.planner.v1";

/** Per-range cache key — distinct ranges never share a localStorage slot. */
function storageKey(range: { from: string; to: string }): string {
  return `${STORAGE_PREFIX}:${range.from}:${range.to}`;
}

type CachedState = {
  range: { from: string; to: string };
  workouts: PlannedWorkout[];
  savedAt: number;
};

function readCache(range: { from: string; to: string }): PlannedWorkout[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey(range));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedState;
    // Belt-and-braces: the key is range-scoped, but still reject a payload
    // whose embedded range doesn't match (stale key format, manual edits).
    if (parsed.range.from !== range.from || parsed.range.to !== range.to) return null;
    // 24h staleness ceiling — fall through to a fresh fetch beyond that.
    if (Date.now() - parsed.savedAt > 24 * 60 * 60 * 1000) return null;
    return parsed.workouts;
  } catch {
    return null;
  }
}

function writeCache(range: { from: string; to: string }, workouts: PlannedWorkout[]): void {
  if (typeof window === "undefined") return;
  try {
    const payload: CachedState = { range, workouts, savedAt: Date.now() };
    window.localStorage.setItem(storageKey(range), JSON.stringify(payload));
  } catch {
    // Best-effort cache; full disks are not our problem here.
  }
}

export type PlannerState = {
  /** True until the first fetch lands (or the localStorage cache paints). */
  loading: boolean;
  /** Network error from the most recent fetch. */
  error: string | null;
  workouts: PlannedWorkout[];
  /**
   * Live active fuel policy snapshot, shipped alongside the workout range
   * (PR-E). Null until the first refresh resolves. Drives optimistic tier
   * recompute on duration edits — when null, the optimistic patch preserves
   * the prior tier and the next server refresh corrects it.
   */
  policy: FuelPolicySnapshot | null;
  /**
   * Whole-account earliest/latest workout dates (range-independent), from the
   * server aggregate on the last fetch. Null until the first refresh resolves
   * (or against a stale server without the field). The calendar window sizes
   * its no-event far edge from `latestYmd` — authoritative from first load,
   * no waiting for far rows to be discovered by scrolling.
   */
  calendarBounds: CalendarBounds | null;
  refresh: () => Promise<void>;
  create: (input: CreatePlannedWorkoutInput) => Promise<PlannedWorkout[]>;
  update: (id: string, patch: UpdatePlannedWorkoutInput) => Promise<PlannedWorkout>;
  remove: (id: string) => Promise<void>;
  removeSeries: (repeatGroupId: string) => Promise<number>;
  /**
   * Remove a whole applied training plan (`planGroupId`). Optimistic with
   * rollback — drops the group's rows from local state immediately so the
   * calendar/upcoming list don't show them through the DELETE round-trip,
   * then deletes server-side. Returns the deleted count.
   */
  removeByPlanGroup: (planGroupId: string) => Promise<number>;
  /**
   * Create many distinct rows in one transaction (copy/paste, duplicate-week,
   * Apply-a-plan). Returns the created rows so the caller can offer Undo via
   * `bulkRemove`. Rows are inserted into the local set after the server confirms
   * (the response carries real ids + server-computed tiers).
   */
  bulkCreate: (items: BulkCreateItem[], planGroupId?: string) => Promise<PlannedWorkout[]>;
  /** Delete many rows by id (paste-undo / Replace). Optimistic with rollback. */
  bulkRemove: (ids: string[]) => Promise<void>;
  /**
   * Atomic "Replace day": delete `deleteIds` and create `items` in ONE `/bulk`
   * transaction. State updates after the server confirms. Returns created rows.
   */
  bulkReplace: (deleteIds: string[], items: BulkCreateItem[]) => Promise<PlannedWorkout[]>;
};

function csrfTokenFromAuth(state: ReturnType<typeof useAuth>["state"]): string | null {
  if (state.kind === "customer" || state.kind === "anon") return state.csrfToken;
  return null;
}

/**
 * Window event that forces every mounted `usePlannedWorkouts` to refetch.
 * For mutations that change workout rows *outside* this hook's own callbacks —
 * e.g. the anon→login race link (`linkTrainingPlanEvent`), which backfills
 * `customerEventId` server-side from `App` — so the calendar/banner pick up the
 * change without waiting for a range change or manual reload.
 */
export const PLANNER_REFRESH_EVENT = "fg:planner-refresh";

export function requestPlannerRefresh(): void {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PLANNER_REFRESH_EVENT));
}

export function usePlannedWorkouts(rangeInput: { from: string; to: string }): PlannerState {
  const { state: authState, refreshCsrf } = useAuth();
  // Callers commonly pass a fresh object literal each render. Re-derive a
  // stable `range` whose identity changes only when `from`/`to` actually
  // change — every callback below can then list `range` honestly without
  // refetching on every render.
  const range = useMemo(
    () => ({ from: rangeInput.from, to: rangeInput.to }),
    [rangeInput.from, rangeInput.to]
  );
  const [workouts, setWorkouts] = useState<PlannedWorkout[]>(() => readCache(range) ?? []);
  const [policy, setPolicy] = useState<FuelPolicySnapshot | null>(null);
  const [calendarBounds, setCalendarBounds] = useState<CalendarBounds | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const res = await listWorkoutsChunked(range, ctrl.signal);
      if (ctrl.signal.aborted) return;
      setWorkouts(res.workouts);
      setPolicy(res.policy);
      // Keep the last known bounds when a stale server omits the field —
      // absent ≠ "no workouts".
      if (res.calendarBounds) setCalendarBounds(res.calendarBounds);
      writeCache(range, res.workouts);
    } catch (err) {
      if (ctrl.signal.aborted || isAbortError(err)) return;
      setError(userFacingError(err, "Could not load workouts"));
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void refresh();
    return () => abortRef.current?.abort();
  }, [refresh]);

  // Refetch when another part of the app signals a planner-affecting change it
  // made outside this hook. Three triggers, all handled identically:
  //  - `PLANNER_REFRESH_EVENT` — direct planner mutations outside this hook's
  //    own callbacks (see `requestPlannerRefresh`, e.g. anon→login race link).
  //  - `PREFERENCES_SAVED_EVENT` / `FUELING_PROFILE_CHANGED_EVENT` — a fueling
  //    profile / preferences save regenerates per-session carb targets + policy
  //    server-side (Lane 5). This hook holds those numbers on the calendar, so
  //    without this listener the grid's session figures stay stale until a
  //    manual reload. Robust fix: covers EVERY opener of the fueling editor, not
  //    just the ones that thread an `onSaved` callback.
  useEffect(() => {
    const onRefresh = () => void refresh();
    window.addEventListener(PLANNER_REFRESH_EVENT, onRefresh);
    window.addEventListener(PREFERENCES_SAVED_EVENT, onRefresh);
    window.addEventListener(FUELING_PROFILE_CHANGED_EVENT, onRefresh);
    return () => {
      window.removeEventListener(PLANNER_REFRESH_EVENT, onRefresh);
      window.removeEventListener(PREFERENCES_SAVED_EVENT, onRefresh);
      window.removeEventListener(FUELING_PROFILE_CHANGED_EVENT, onRefresh);
    };
  }, [refresh]);

  const requireCsrf = useCallback((): string => {
    const token = csrfTokenFromAuth(authState);
    if (!token) {
      // Throw a typed ApiError with a user-safe message so the catch in callers
      // (and downstream `userFacingError`) renders friendly copy instead of
      // the developer-facing "No CSRF token available" string. We DO NOT write
      // to the page-level `error` state here — that's reserved for fetch
      // failures so a failed mutation can't blow up the calendar banner.
      throw new ApiError(401, "csrf_unavailable", "Refresh to continue", {
        userSafeMessage: "Your session is still loading. Refresh and try again."
      });
    }
    return token;
  }, [authState]);

  const create = useCallback<PlannerState["create"]>(
    async (input) => {
      const csrf = requireCsrf();
      const inRange =
        input.date >= range.from && input.date <= range.to;
      // Mutations no longer poison the fetch-level `error` state — callers
      // (modals / toasts) surface their own message via `userFacingError`.
      // The createWorkout rejection bubbles up to the caller unchanged.
      const { workouts: created } = await createWorkout(input, csrf, refreshCsrf);
      if (created.length > 0) {
        setWorkouts((curr) => {
          const next = [...curr];
          for (const row of created) {
            // Only insert into the working set if the row is inside the
            // currently-visible range — otherwise we'd render a chip with
            // no calendar cell to land in. The next refresh picks it up if
            // the customer scrolls there.
            if (row.date >= range.from && row.date <= range.to) next.push(row);
          }
          next.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
          writeCache(range, next);
          return next;
        });
      } else if (inRange) {
        // The server expanded a repeat that landed outside our range, or no
        // rows were created at all. Refresh to be safe.
        void refresh();
      }
      // Confirmed success — invalidate the downstream graph so journey metadata
      // (drop counts, stale chips) and the 60s fuel-plan cache don't lag until
      // an unrelated refetch (deletes already do this; see useMyTrainingDeletes).
      notifyJourneyChanged();
      invalidateFuelPlanCache();
      return created;
    },
    [range, requireCsrf, refreshCsrf, refresh]
  );

  const update = useCallback<PlannerState["update"]>(
    async (id, patch) => {
      const csrf = requireCsrf();
      // Targeted rollback: capture ONLY the pre-mutation row(s) we touch, not
      // the whole list. A concurrent successful mutation on a different row
      // therefore can't be undone by this one's failure path.
      let prevRow: PlannedWorkout | null = null;
      // Optimistic: apply patch locally first, then revert if the request fails.
      // PR-E: when durationMin moves, recompute the tier client-side via the
      // SAME classifier the server uses (`classifyFuelTierFromPolicy`) so the
      // chip doesn't briefly read its pre-edit tier between PATCH and refresh.
      // Falls back to preserving the prior tier when the policy hasn't loaded
      // yet (first-paint edits) — safer than guessing.
      setWorkouts((curr) => {
        const next = curr.map((w) => {
          if (w.id !== id) return w;
          prevRow = w;
          const nextDuration = patch.durationMin ?? w.durationMin;
          const nextTier =
            patch.durationMin != null && policy
              ? classifyFuelTierFromPolicy(nextDuration, policy)
              : w.tier;
          return {
            ...w,
            ...patch,
            tier: nextTier,
            updatedAt: new Date().toISOString()
          };
        });
        writeCache(range, next);
        return next;
      });
      try {
        const updated = await patchWorkout(id, patch, csrf, refreshCsrf);
        setWorkouts((curr) => {
          // Keep the row in the local cache regardless of whether the new date
          // is inside the *current* range. The Planner's `weekCount` tracks the
          // latest workout date and extends the rendered/fetched range to
          // include far-out dates, so dropping here would silently hide an
          // edit the user just made (rare, only possible via the modal date
          // picker placing a date past the current padding — but real).
          const next = curr.map((w) => (w.id === id ? updated : w));
          writeCache(range, next);
          return next;
        });
        // Confirmed success — invalidate the downstream graph (journey metadata
        // + fuel-plan cache). Only on the resolved PATCH, never on rollback.
        notifyJourneyChanged();
        invalidateFuelPlanCache();
        return updated;
      } catch (err) {
        // Per-id rollback — preserves any other in-flight optimistic edits.
        setWorkouts((curr) => {
          if (!prevRow) return curr;
          const next = curr.map((w) => (w.id === id ? prevRow! : w));
          writeCache(range, next);
          return next;
        });
        throw err;
      }
    },
    [range, requireCsrf, refreshCsrf, policy]
  );

  const remove = useCallback<PlannerState["remove"]>(
    async (id) => {
      const csrf = requireCsrf();
      let prevRow: PlannedWorkout | null = null;
      let prevIndex = -1;
      setWorkouts((curr) => {
        const idx = curr.findIndex((w) => w.id === id);
        if (idx >= 0) {
          prevRow = curr[idx];
          prevIndex = idx;
        }
        const next = curr.filter((w) => w.id !== id);
        writeCache(range, next);
        return next;
      });
      try {
        await deleteWorkout(id, csrf, refreshCsrf);
      } catch (err) {
        // Per-id rollback — re-insert at the original position only if the row
        // isn't already back (a refresh may have rehydrated it in the meantime).
        setWorkouts((curr) => {
          if (!prevRow) return curr;
          if (curr.some((w) => w.id === id)) return curr;
          const next = [...curr];
          const insertAt = Math.min(Math.max(prevIndex, 0), next.length);
          next.splice(insertAt, 0, prevRow);
          writeCache(range, next);
          return next;
        });
        throw err;
      }
    },
    [range, requireCsrf, refreshCsrf]
  );

  const removeSeries = useCallback<PlannerState["removeSeries"]>(
    async (repeatGroupId) => {
      const csrf = requireCsrf();
      let prevSeries: PlannedWorkout[] = [];
      setWorkouts((curr) => {
        prevSeries = curr.filter((w) => w.repeatGroupId === repeatGroupId);
        const next = curr.filter((w) => w.repeatGroupId !== repeatGroupId);
        writeCache(range, next);
        return next;
      });
      try {
        return await apiDeleteSeries(repeatGroupId, csrf, refreshCsrf);
      } catch (err) {
        // Per-series rollback — re-merge the series rows and re-sort. Other
        // concurrent edits in `curr` are preserved.
        setWorkouts((curr) => {
          if (prevSeries.length === 0) return curr;
          const have = new Set(curr.map((w) => w.id));
          const merged = [...curr, ...prevSeries.filter((w) => !have.has(w.id))];
          merged.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
          writeCache(range, merged);
          return merged;
        });
        throw err;
      }
    },
    [range, requireCsrf, refreshCsrf]
  );

  const removeByPlanGroup = useCallback<PlannerState["removeByPlanGroup"]>(
    async (planGroupId) => {
      const csrf = requireCsrf();
      let prevGroup: PlannedWorkout[] = [];
      setWorkouts((curr) => {
        prevGroup = curr.filter((w) => w.planGroupId === planGroupId);
        if (prevGroup.length === 0) return curr;
        const next = curr.filter((w) => w.planGroupId !== planGroupId);
        writeCache(range, next);
        return next;
      });
      try {
        return await removeTrainingPlan(planGroupId, csrf, refreshCsrf);
      } catch (err) {
        // Rollback — re-merge the group's rows that aren't already back, re-sort.
        setWorkouts((curr) => {
          if (prevGroup.length === 0) return curr;
          const have = new Set(curr.map((w) => w.id));
          const merged = [...curr, ...prevGroup.filter((w) => !have.has(w.id))];
          merged.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
          writeCache(range, merged);
          return merged;
        });
        throw err;
      }
    },
    [range, requireCsrf, refreshCsrf]
  );

  const bulkCreate = useCallback<PlannerState["bulkCreate"]>(
    async (items, planGroupId) => {
      const csrf = requireCsrf();
      const created = await apiBulkCreateDistinct(items, csrf, refreshCsrf, planGroupId);
      if (created.length > 0) {
        setWorkouts((curr) => {
          const next = [...curr];
          for (const row of created) {
            // Only land rows inside the visible range — the next refresh / range
            // growth picks up any that fall outside (same rule as `create`).
            if (row.date >= range.from && row.date <= range.to) next.push(row);
          }
          next.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
          writeCache(range, next);
          return next;
        });
      }
      // Confirmed success — invalidate the downstream graph (journey metadata +
      // fuel-plan cache), matching the delete path's notifyJourneyChanged.
      notifyJourneyChanged();
      invalidateFuelPlanCache();
      return created;
    },
    [range, requireCsrf, refreshCsrf]
  );

  const bulkRemove = useCallback<PlannerState["bulkRemove"]>(
    async (ids) => {
      const csrf = requireCsrf();
      const idSet = new Set(ids);
      let prev: PlannedWorkout[] = [];
      setWorkouts((curr) => {
        prev = curr.filter((w) => idSet.has(w.id));
        const next = curr.filter((w) => !idSet.has(w.id));
        writeCache(range, next);
        return next;
      });
      try {
        await apiBulkDelete(ids, csrf, refreshCsrf);
      } catch (err) {
        // Rollback — re-merge the removed rows that aren't already back.
        setWorkouts((curr) => {
          if (prev.length === 0) return curr;
          const have = new Set(curr.map((w) => w.id));
          const merged = [...curr, ...prev.filter((w) => !have.has(w.id))];
          merged.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
          writeCache(range, merged);
          return merged;
        });
        throw err;
      }
    },
    [range, requireCsrf, refreshCsrf]
  );

  const bulkReplace = useCallback<PlannerState["bulkReplace"]>(
    async (deleteIds, items) => {
      const csrf = requireCsrf();
      const res = await apiBulkReplace(deleteIds, items, csrf, refreshCsrf);
      const delSet = new Set(deleteIds);
      setWorkouts((curr) => {
        const next = curr.filter((w) => !delSet.has(w.id));
        for (const row of res.created) {
          if (row.date >= range.from && row.date <= range.to) next.push(row);
        }
        next.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
        writeCache(range, next);
        return next;
      });
      // Confirmed success — invalidate the downstream graph (journey metadata +
      // fuel-plan cache). Covers both the delete and create halves of the swap.
      notifyJourneyChanged();
      invalidateFuelPlanCache();
      return res.created;
    },
    [range, requireCsrf, refreshCsrf]
  );

  return useMemo(
    () => ({
      loading,
      error,
      workouts,
      policy,
      calendarBounds,
      refresh,
      create,
      update,
      remove,
      removeSeries,
      removeByPlanGroup,
      bulkCreate,
      bulkRemove,
      bulkReplace
    }),
    [
      loading,
      error,
      workouts,
      policy,
      calendarBounds,
      refresh,
      create,
      update,
      remove,
      removeSeries,
      removeByPlanGroup,
      bulkCreate,
      bulkRemove,
      bulkReplace
    ]
  );
}
